/**
 * Opening hours, in the shape a person edits them and in the shape the manifest
 * carries them.
 *
 * The stored form is a flat list of `{ day, open, close }` entries where a close
 * at or before the open wraps past midnight — Friday 11:00 to 02:30 is a normal
 * Friday for a pizza shop, not an edge case. The editable form groups those
 * entries by day and allows several per day, because the resolver unions every
 * entry that matches, so a lunchtime and an evening service on the same day are
 * expressible and must not be silently collapsed on the way through this panel.
 *
 * Two consequences of the stored form are worth stating plainly, because both
 * surprise people:
 *
 *   - An empty list means the screens run around the clock. It does not mean the
 *     store is never open. Clearing every day is therefore a real decision and
 *     the editor says so.
 *   - An entry whose close equals its open covers a full 24 hours, which is what
 *     `isWithinWeeklyWindow` does with such a window.
 */

import { isHhMm, parseHhMm } from '@pbtv/shared';
import type { IsoDayOfWeekValue, OpeningHoursEntry } from '@pbtv/shared';
import { formatMinutes } from './format.js';

export const MINUTES_PER_DAY = 1440;
export const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY;

export const ISO_DAYS: readonly IsoDayOfWeekValue[] = [1, 2, 3, 4, 5, 6, 7];

export const DAY_NAMES: Readonly<Record<IsoDayOfWeekValue, string>> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

export const DAY_ABBREVIATIONS: Readonly<Record<IsoDayOfWeekValue, string>> = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun',
};

export interface HoursPeriod {
  open: string;
  close: string;
}

/** One row of the editor: a weekday and every period the store trades that day. */
export interface DayHours {
  day: IsoDayOfWeekValue;
  periods: HoursPeriod[];
}

export const DEFAULT_PERIOD: HoursPeriod = { open: '11:00', close: '22:00' };

// ---------------------------------------------------------------------------
// Stored list <-> editable week
// ---------------------------------------------------------------------------

export function emptyWeek(): DayHours[] {
  return ISO_DAYS.map((day) => ({ day, periods: [] }));
}

/** Groups stored entries into the seven rows the editor shows, in weekday order. */
export function toWeek(entries: readonly OpeningHoursEntry[]): DayHours[] {
  const week = emptyWeek();
  for (const entry of entries) {
    const row = week[entry.day - 1];
    if (row === undefined) continue;
    row.periods.push({ open: entry.open, close: entry.close });
  }
  for (const row of week) {
    row.periods.sort((left, right) => left.open.localeCompare(right.open));
  }
  return week;
}

/** Flattens the editor back to the stored list, dropping days with no periods. */
export function fromWeek(week: readonly DayHours[]): OpeningHoursEntry[] {
  const entries: OpeningHoursEntry[] = [];
  for (const day of ISO_DAYS) {
    const row = week.find((candidate) => candidate.day === day);
    if (row === undefined) continue;
    for (const period of row.periods) {
      entries.push({ day, open: period.open, close: period.close });
    }
  }
  return entries;
}

// ---------------------------------------------------------------------------
// One period
// ---------------------------------------------------------------------------

export function isValidPeriod(period: HoursPeriod): boolean {
  return isHhMm(period.open) && isHhMm(period.close);
}

/**
 * How long a period lasts, in minutes. A close at or before the open wraps into
 * the next day, and a close equal to the open is a full 24 hours rather than a
 * zero-length window, which is what the shared window test does with it.
 */
export function periodMinutes(period: HoursPeriod): number | null {
  if (!isValidPeriod(period)) return null;
  const open = parseHhMm(period.open);
  const close = parseHhMm(period.close);
  return ((close - open + MINUTES_PER_DAY - 1) % MINUTES_PER_DAY) + 1;
}

export function crossesMidnight(period: HoursPeriod): boolean {
  if (!isValidPeriod(period)) return false;
  return parseHhMm(period.close) <= parseHhMm(period.open);
}

/** "11:00 – 02:30 next day", the way a Friday is actually written on the door. */
export function formatPeriodRange(period: HoursPeriod): string {
  const minutes = periodMinutes(period);
  if (minutes === null) return 'Enter both times as 24-hour HH:MM';
  if (minutes === MINUTES_PER_DAY) return 'Open 24 hours';
  return `${period.open} – ${period.close}${crossesMidnight(period) ? ' next day' : ''}`;
}

/** The same, with how long it leaves the screens on: "11:00 – 02:30 next day · 15h 30m". */
export function describePeriod(period: HoursPeriod): string {
  const minutes = periodMinutes(period);
  const range = formatPeriodRange(period);
  if (minutes === null || minutes === MINUTES_PER_DAY) return range;
  return `${range} · ${formatMinutes(minutes)}`;
}

/** The whole day in one line, with durations — for the day-by-day breakdown. */
export function describeDay(periods: readonly HoursPeriod[]): string {
  if (periods.length === 0) return 'Closed';
  return periods.map(describePeriod).join(' and ');
}

/** The whole day without durations, for the one-line week summary. */
export function summariseDay(periods: readonly HoursPeriod[]): string {
  if (periods.length === 0) return 'closed';
  return periods.map(formatPeriodRange).join(', ');
}

// ---------------------------------------------------------------------------
// Validation
//
// Overlaps are checked across the whole week rather than within a day, because a
// Friday period that runs to 02:30 lands inside Saturday and can collide with a
// Saturday brunch shift. Every period becomes one or two segments on a
// 0..10079 minute line and the segments are compared pairwise; a week holds at
// most a couple of dozen, so the quadratic comparison is free.
// ---------------------------------------------------------------------------

export interface HoursIssue {
  day: IsoDayOfWeekValue;
  message: string;
}

interface WeekSegment {
  day: IsoDayOfWeekValue;
  index: number;
  period: HoursPeriod;
  start: number;
  end: number;
}

function weekSegments(week: readonly DayHours[]): WeekSegment[] {
  const segments: WeekSegment[] = [];

  for (const row of week) {
    row.periods.forEach((period, index) => {
      const minutes = periodMinutes(period);
      if (minutes === null) return;

      const start = (row.day - 1) * MINUTES_PER_DAY + parseHhMm(period.open);
      const end = start + minutes;
      if (end <= MINUTES_PER_WEEK) {
        segments.push({ day: row.day, index, period, start, end });
      } else {
        // Sunday night into Monday morning: the tail belongs at the top of the
        // same repeating week, not past its end.
        segments.push({ day: row.day, index, period, start, end: MINUTES_PER_WEEK });
        segments.push({ day: row.day, index, period, start: 0, end: end - MINUTES_PER_WEEK });
      }
    });
  }

  return segments;
}

function overlaps(left: WeekSegment, right: WeekSegment): boolean {
  return left.start < right.end && right.start < left.end;
}

export function weekIssues(week: readonly DayHours[]): HoursIssue[] {
  const issues: HoursIssue[] = [];

  for (const row of week) {
    for (const period of row.periods) {
      if (!isValidPeriod(period)) {
        issues.push({
          day: row.day,
          message: `${DAY_NAMES[row.day]}: enter both times as 24-hour HH:MM, for example 17:30.`,
        });
      }
    }
  }

  const segments = weekSegments(week);
  const reported = new Set<string>();

  for (let i = 0; i < segments.length; i += 1) {
    for (let j = i + 1; j < segments.length; j += 1) {
      const left = segments[i];
      const right = segments[j];
      if (left === undefined || right === undefined) continue;
      if (left.day === right.day && left.index === right.index) continue;
      if (!overlaps(left, right)) continue;

      const key = [left.day, left.index, right.day, right.index].join(':');
      if (reported.has(key)) continue;
      reported.add(key);

      issues.push({
        day: left.day,
        message:
          `${DAY_NAMES[left.day]} ${left.period.open}–${left.period.close} overlaps ` +
          `${DAY_NAMES[right.day]} ${right.period.open}–${right.period.close}. ` +
          'Merge them into one period.',
      });
    }
  }

  return issues;
}

// ---------------------------------------------------------------------------
// Week summary
// ---------------------------------------------------------------------------

function dayKey(periods: readonly HoursPeriod[]): string {
  return periods.map((period) => `${period.open}-${period.close}`).join(',');
}

function labelRun(from: IsoDayOfWeekValue, to: IsoDayOfWeekValue): string {
  if (from === to) return DAY_ABBREVIATIONS[from];
  if (from === 1 && to === 7) return 'Every day';
  return `${DAY_ABBREVIATIONS[from]}–${DAY_ABBREVIATIONS[to]}`;
}

/**
 * The whole week in one line, collapsing consecutive days that trade the same
 * hours: "Mon–Thu 11:00 – 22:00 · Fri–Sat 11:00 – 02:30 next day · Sun closed".
 */
export function summariseOpeningHours(entries: readonly OpeningHoursEntry[]): string {
  if (entries.length === 0) return 'No hours set — screens play around the clock';

  const week = toWeek(entries);
  const parts: string[] = [];

  let runStart: IsoDayOfWeekValue | null = null;
  let runEnd: IsoDayOfWeekValue | null = null;
  let runKey = '';
  let runPeriods: HoursPeriod[] = [];

  const flush = (): void => {
    if (runStart === null || runEnd === null) return;
    parts.push(`${labelRun(runStart, runEnd)} ${summariseDay(runPeriods)}`);
  };

  for (const day of ISO_DAYS) {
    const row = week[day - 1];
    const periods = row?.periods ?? [];
    const key = dayKey(periods);

    if (runStart !== null && key === runKey) {
      runEnd = day;
      continue;
    }

    flush();
    runStart = day;
    runEnd = day;
    runKey = key;
    runPeriods = periods;
  }
  flush();

  return parts.join(' · ');
}

/**
 * A store with no entries at all is open around the clock. That is a legitimate
 * setting and also the single easiest thing to arrive at by accident, so the
 * editor calls it out instead of saving it quietly.
 */
export function isAlwaysOpen(week: readonly DayHours[]): boolean {
  return week.every((row) => row.periods.length === 0);
}
